<template>
    <pendo-menu
        ref="menu"
        class="global-create"
        placement="right-start"
        container=".side-nav"
        :distance="8"
        :items="filteredItems"
        :class="{ expanded }"
        @open="isMenuOpen = true"
        @close="isMenuOpen = false"
        @click-item="onItemClick">
        <template #trigger="{ toggle }">
            <pendo-button
                v-pendo-tooltip="{
                    content: !expanded && tooltipContent,
                    arrow: true,
                    placement: 'right',
                }"
                pendo-analytics="global-create-trigger"
                data-cy="global-create-trigger"
                type="secondary"
                class="global-create__trigger"
                :class="{
                    'global-create__trigger--white-label': useWhiteLabelStyling,
                    'is-open': isMenuOpen,
                }"
                :style="whiteLabelButtonStyle"
                icon="plus"
                icon-size="16"
                size="small"
                :label="expanded ? $t('globalCreate.Create new') : ''"
                :aria-label="$t('globalCreate.Create new')"
                :aria-expanded="isMenuOpen"
                @click="toggle"
                @mouseover="setTooltipContent" />
        </template>
        <template #item="{ item }">
            <pendo-router-link
                class="global-create__option"
                role="menuitem"
                tabindex="0"
                :to="item.to"
                :replace="replaceRoute(item.to)"
                @click.capture.stop.prevent="handleLinkClick(item)">
                <pendo-icon
                    v-bind="item.icon"
                    aria-hidden="true" />
                <div class="global-create__option-text">
                    <div>
                        {{ item.label }}
                    </div>
                    <div
                        v-if="item.description"
                        class="global-create__option-description">
                        {{ item.description }}
                    </div>
                </div>
            </pendo-router-link>
        </template>
    </pendo-menu>
</template>

<script>
import { PendoMenu, PendoButton, PendoIcon, PendoTooltip } from '@pendo/components-vue3';
import { mapState as mapPiniaState } from 'pinia';
import { useAppsStore } from '@/stores/apps.store';

import { DASHBOARD_CREATE_LOCATION } from '@/dashboards-v2/helpers/common.util';
import { v4 as uuid } from 'uuid';
import { canCreateJourney } from '@/vuex/modules/journeys/journeys-permissions.utils';
import { canCreateGuide } from '@/guides/helpers/common.util';
import { $can } from '@pendo/permissions';
import * as RoutePermissions from '@/vue-router/route-permissions.utils';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';
import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';
import { getAppsWithSurveyCreatePermission } from '@/surveys/utils/surveys.utils.js';
import { useSubscriptionStore } from '@/stores/subscription.store';

export default {
    name: 'GlobalCreate',
    components: {
        PendoMenu,
        PendoButton,
        PendoIcon,
        PendoRouterLink,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        expanded: {
            type: Boolean,
            default: false,
        },
        openPopover: {
            type: String,
            default: '',
        },
    },
    emits: ['create'],
    data () {
        return {
            tooltipContent: null,
            isMenuOpen: false,
        };
    },
    computed: {
        ...mapPiniaState(useAppsStore, {
            apps: 'allWebPlusIntegratedMobileApps',
            integratedApps: 'webLikeList',
        }),
        ...mapPiniaState(useSubscriptionStore, {
            isFreeAndOverMauLimit: 'isFreeAndOverMauLimit',
            getFeatureFlag: 'getFeatureFlag',
            trainingAttributes: 'trainingAttributes',
        }),
        items () {
            return [
                {
                    id: uuid(),
                    label: this.$t('globalCreate.Dashboard'),
                    icon: {
                        type: 'dashboard',
                        size: 16,
                    },
                    analytics: 'global-create-dashboard',
                    to: {
                        name: 'Dashboard',
                        params: {
                            id: 'new',
                        },
                        query: {
                            location: DASHBOARD_CREATE_LOCATION.GLOBAL_CREATE,
                        },
                    },
                    tabIndex: -1,
                },
                {
                    id: uuid(),
                    label: this.$t('globalCreate.Segment'),
                    icon: {
                        type: 'pie-chart',
                        size: 16,
                    },
                    analytics: 'global-create-segment',
                    to: {
                        name: 'Segments',
                        query: {
                            segment: 'createNew',
                            location: 'globalCreate',
                        },
                    },
                    hasPermissions: () => !this.isFreeAndOverMauLimit,
                    tabIndex: -1,
                },
                {
                    id: uuid(),
                    label: this.$t('globalCreate.Report'),
                    icon: {
                        type: 'bar-chart-big',
                        size: 16,
                    },
                    tabIndex: -1,
                    items: [
                        {
                            id: uuid(),
                            label: this.$t('globalCreate.Data Explorer'),
                            description: this.$t(
                                'globalCreate.Query usage data and visualize comparisons, breakdowns, and calculations.',
                            ),
                            icon: {
                                type: 'area-chart',
                                size: 16,
                            },
                            analytics: 'global-create-data-explorer',
                            to: {
                                name: 'DataExplorer',
                                query: { globalCreate: 'true' },
                            },
                            hasPermissions: () => !RoutePermissions.hasDataExplorerAccessRedirect(),
                        },
                        {
                            id: uuid(),
                            label: this.$t('globalCreate.Retention'),
                            description: this.$t(
                                'globalCreate.Analyze how many users come back to your app or feature each month or week.',
                            ),
                            icon: {
                                type: 'retention-2',
                                size: 16,
                            },
                            analytics: 'global-create-retention',
                            to: {
                                name: 'RetentionDetails',
                                query: { globalCreate: 'true' },
                            },
                            hasPermissions: () => !RoutePermissions.hasRetentionAccessRedirect(),
                        },
                        {
                            id: uuid(),
                            label: this.$t('globalCreate.Funnel'),
                            description: this.$t(
                                'globalCreate.Analyze conversion rate across a set of specified steps.',
                            ),
                            icon: {
                                type: 'funnels',
                                size: 16,
                            },
                            analytics: 'global-create-funnel',
                            to: {
                                name: 'FunnelDetails',
                                query: { globalCreate: 'true' },
                            },
                            hasPermissions: () => !RoutePermissions.hasFunnelsAccessRedirect(),
                        },
                        {
                            id: uuid(),
                            label: this.$t('globalCreate.Path'),
                            description: this.$t(
                                'globalCreate.Analyze the different actions users take before or after a specified action.',
                            ),
                            icon: {
                                type: 'paths',
                                size: 16,
                            },
                            analytics: 'global-create-path',
                            to: {
                                name: 'PathDetails',
                                query: { globalCreate: 'true' },
                            },
                            hasPermissions: () => !RoutePermissions.hasPathsAccessRedirect(),
                        },
                        {
                            id: uuid(),
                            label: this.$t('globalCreate.Workflow'),
                            description: this.$t(
                                'globalCreate.Track and analyze processes and identify optimal user journeys.',
                            ),
                            icon: {
                                type: 'workflows',
                                size: 16,
                            },
                            analytics: 'global-create-workflow',
                            to: {
                                name: 'CreateWorkflow',
                                query: { globalCreate: 'true' },
                            },
                            hasPermissions: () => !RoutePermissions.hasWorkflowsRedirect(),
                        },
                        {
                            id: uuid(),
                            label: this.$t('globalCreate.Visitors'),
                            description: this.$t(
                                'globalCreate.View visitors in a segment and analyze their metadata, usage, and poll responses.',
                            ),
                            icon: {
                                type: 'user',
                                size: 16,
                            },
                            analytics: 'global-create-visitors',
                            to: {
                                name: 'VisitorList',
                                query: { globalCreate: 'true' },
                            },
                            hasPermissions: () => !RoutePermissions.hasVisitorReportsRedirect(),
                        },
                        {
                            id: uuid(),
                            label: this.$t('globalCreate.Accounts'),
                            description: this.$t(
                                'globalCreate.View accounts in a segment and analyze metadata and aggregated usage across visitors.',
                            ),
                            icon: {
                                type: 'building-1',
                                size: 16,
                            },
                            analytics: 'global-create-accounts',
                            to: {
                                name: 'AccountList',
                                query: { globalCreate: 'true' },
                            },
                            hasPermissions: () => !RoutePermissions.hasAccountsRedirect(),
                        },
                        {
                            id: uuid(),
                            label: this.$t('globalCreate.Parent Accounts'),
                            description: this.$t(
                                'globalCreate.View parent accounts in a segment and analyze metadata and aggregated usage across visitors.',
                            ),
                            icon: {
                                type: 'building-1',
                                size: 16,
                            },
                            analytics: 'global-create-parent-accounts',
                            to: {
                                name: 'ParentAccountList',
                                query: { globalCreate: 'true' },
                            },
                            hasPermissions: () => !RoutePermissions.hasParentAccountsRedirect(),
                        },
                    ],
                },
                {
                    id: uuid(),
                    label: this.$t('globalCreate.Guide'),
                    icon: {
                        type: 'guide',
                        size: 16,
                    },
                    analytics: 'global-create-guide',
                    to: {
                        name: 'Guides',
                        query: {
                            openChooser: 'true',
                            location: 'globalCreate',
                        },
                    },
                    hasPermissions: () =>
                        !RoutePermissions.hasBasicGuidesRedirect() &&
                        this.hasP2DesignerAccess &&
                        this.canCreateGuide &&
                        !this.isFreeAndOverMauLimit,
                    tabIndex: -1,
                },
                {
                    id: uuid(),
                    label: this.$t('globalCreate.Journey'),
                    icon: {
                        type: 'orchestrate',
                        size: 22,
                        strokeWidth: 1.5,
                    },
                    analytics: 'global-create-journey',
                    to: {
                        name: 'Orchestrate',
                        query: {
                            openChooser: 'true',
                            location: 'globalCreate',
                        },
                    },
                    hasPermissions: () => !RoutePermissions.hasJourneysRedirect() && this.canCreateJourney,
                    tabIndex: -1,
                },
                {
                    id: uuid(),
                    label: this.$t('globalCreate.Survey'),
                    icon: {
                        type: 'surveys',
                        size: 16,
                    },
                    analytics: 'global-create-survey',
                    to: {
                        name: 'SurveyCreate',
                        query: {
                            location: 'globalCreate',
                        },
                    },
                    hasPermissions: () =>
                        !RoutePermissions.hasSurveysRedirect() &&
                        getAppsWithSurveyCreatePermission(this.integratedApps).length > 0,
                    tabIndex: -1,
                },
                {
                    id: uuid(),
                    label: this.$t('globalCreate.Feedback item'),
                    icon: {
                        type: 'listen',
                        size: 16,
                    },
                    analytics: 'global-create-feedback',
                    to: {
                        name: 'ValidateFeedbackSavedViews',
                        query: {
                            context: 'createFeedbackForm',
                            globalCreate: 'true',
                        },
                    },
                    hasPermissions: () => !RoutePermissions.hasFeedbackSourcesRedirect(),
                    tabIndex: -1,
                },
                {
                    id: uuid(),
                    label: this.$t('globalCreate.Tag'),
                    icon: {
                        type: 'feature',
                        size: 16,
                    },
                    analytics: 'global-create-tag',
                    to: {
                        name: 'Features',
                        query: {
                            autoLaunchP2Designer: 'true',
                            location: 'globalCreate',
                        },
                    },
                    tabIndex: -1,
                    // only checking feature for now since we're opening the tagging modal with the feature type
                    hasPermissions: () =>
                        !RoutePermissions.hasOEMProductAnalyticsRedirect() &&
                        this.hasP2DesignerAccess &&
                        this.canCreateFeature,
                },
            ];
        },
        filteredItems () {
            return this.items.reduce((acc, item) => {
                if (item.hasPermissions && !item.hasPermissions()) {
                    return acc;
                }

                if (item.items) {
                    const filteredSubItems = item.items.filter((subItem) =>
                        !subItem.hasPermissions || subItem.hasPermissions(),
                    );

                    if (filteredSubItems.length > 0) {
                        acc.push({
                            ...item,
                            items: filteredSubItems,
                        });
                    }
                } else {
                    acc.push(item);
                }

                return acc;
            }, []);
        },
        canCreateGuide () {
            return this.apps.some(({ id }) => canCreateGuide(id));
        },
        canCreateJourney () {
            return this.apps.some(({ id }) => canCreateJourney({ appId: id }));
        },
        hasP2DesignerAccess () {
            return this.getFeatureFlag('enableP2Access');
        },
        canCreateFeature () {
            if (this.hasPurchasedAdoptEndUser && !this.$productLine(PRODUCT_LINES.ADOPT, 'taggingEnabled')) {
                return false;
            }

            return this.apps.some(({ id }) =>
                $can(id)
                    .entity('feature')
                    .action('create'),
            );
        },
        hasPurchasedAdoptEndUser () {
            return this.$productLine(PRODUCT_LINES.ADOPT);
        },
        whiteLabelPrimaryColor () {
            return this.hasPurchasedAdoptEndUser ? this.trainingAttributes.whiteLabelSettings?.primaryColor : null;
        },
        useWhiteLabelStyling () {
            return Boolean(this.hasPurchasedAdoptEndUser && this.whiteLabelPrimaryColor);
        },
        whiteLabelButtonStyle () {
            if (!this.useWhiteLabelStyling) {
                return {};
            }

            return {
                '--white-label-primary-color': this.whiteLabelPrimaryColor,
                '--white-label-hover-color': `color-mix(in srgb, ${this.whiteLabelPrimaryColor} 85%, var(--static-action-text-default-base))`,
                '--white-label-focus-color': `color-mix(in srgb, ${this.whiteLabelPrimaryColor} 85%, var(--static-action-text-default-base))`,
            };
        },
    },
    watch: {
        openPopover (newVal) {
            if (newVal && this.isMenuOpen) {
                this.$refs.menu.toggle();
                this.tooltipContent = null;
            }
        },
    },
    methods: {
        onItemClick (item) {
            this.tooltipContent = null;

            const href = this.$router.pendo.toPath(item.to);

            if (this.replaceRoute(item.to)) {
                this.$router.replace(href);
            } else {
                this.$router.push(href);
            }
        },
        handleLinkClick (item) {
            if (item.items) {
                return;
            }

            this.$refs.menu?.onLeafClick(item);
        },
        setTooltipContent () {
            this.tooltipContent = this.$t('globalCreate.Create new');
        },
        replaceRoute (route) {
            if (!route) {
                return false;
            }

            return this.$router.pendo.toPath(route) === this.$route.path;
        },
    },
};
</script>

<style lang="less" scoped>
.global-create {
    margin-bottom: 16px;
    width: 100%;
    height: 32px;

    &.expanded {
        padding: 0 24px;
    }

    &:not(.expanded) {
        display: flex;
        justify-content: center;

        .global-create__trigger :deep(.pendo-icon) {
            transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        .global-create__trigger.is-open :deep(.pendo-icon) {
            transform: rotate(45deg);
        }
    }

    [data-popper-shown] .global-create__trigger--current-ui,
    .global-create__trigger--current-ui:hover {
        background: var(--static-button-background-primary-hover);
    }

    [data-popper-shown] .global-create__trigger--white-label,
    .global-create__trigger--white-label:hover {
        background: var(--white-label-hover-color);
    }

    &__trigger {
        border-radius: 5px;
        width: 100%;

        &--current-ui {
            background: var(--static-button-background-primary-base);
            border-color: var(--static-button-border-primary-base) !important;
            color: var(--static-text-body-inverse-base);

            &:focus-visible {
                outline: 2px solid var(--static-button-focus-ring-base);
            }

            &:hover {
                color: var(--static-text-body-inverse-base);
            }
        }

        &--white-label {
            background: var(--white-label-primary-color);
            border-color: var(--white-label-primary-color) !important;
            color: var(--static-text-body-inverse-base);

            &:focus-visible {
                outline: 2px solid var(--white-label-focus-color);
            }

            &:hover {
                color: var(--static-text-body-inverse-base);
            }
        }
    }

    &__option {
        display: flex;
        align-items: center;
        flex-grow: 1;
        text-decoration: none;
        color: var(--text-body-default-base);
        font-weight: 400;

        .pendo-icon__orchestrate {
            margin-left: -4px;
        }

        .pendo-icon {
            margin-right: 10px;
        }

        &-text {
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding: 8px 0;
        }

        &-description {
            font-size: 12px;
            color: var(--surface-secondary-subtle-base);
            line-height: 1.4;
        }
    }
}
</style>
